import React from 'react';
import {
  Alert,
  PaymentConfig,
  Service,
  UUIDv4,
} from '@quality24/inpatient-typings';
import { Button, Modal, Text, ListGroup } from '@quality24/design-system';
import { useHTMLParser } from '@quality24/react-hooks';
import { useTranslation } from 'react-i18next';
import useI18nResolvers from '@/inpatient-patient-pwa/i18n/useI18nResolvers';

type PatientCallReasonFragment = Required<
  Pick<
    Service,
    | 'id'
    | 'reason'
    | 'reasonI18N'
    | 'modifier'
    | 'customizationType'
    | 'customizationData'
    | 'actions'
    | 'icons'
    | 'conditional'
  >
>;

interface ComposedPatientCallReason extends PatientCallReasonFragment {
  groupId?: UUIDv4;
  lastAlert?: Required<Pick<Alert, 'createdAt'>> | null;
  activeAlert?: Pick<Alert, 'id'>;
  hasChildren?: boolean;
  children?: Array<ComposedPatientCallReason>;
  paymentConfig?: Pick<PaymentConfig, 'id' | 'data'> | null;
}

export interface Props {
  className?: string;
  show: boolean;
  reason: ComposedPatientCallReason;
  onHide: () => void;
  onSelect: (reason: ComposedPatientCallReason) => void;
}

const PatientCallSelectorModal: React.FunctionComponent<Props> = ({
  className,
  show,
  reason,
  onHide,
  onSelect,
}) => {
  const { t } = useTranslation();
  const { resolveI18n } = useI18nResolvers();
  const [value, setValue] = React.useState<string | undefined>();
  const htmlParser = useHTMLParser();

  const services = React.useMemo(
    () =>
      reason.children?.filter((child) => {
        const innerChildren = child.hasChildren
          ? child.children?.filter((child2) => !child2.activeAlert)
          : undefined;
        return (
          !child.activeAlert && (!innerChildren || innerChildren.length > 0)
        );
      }) || [],
    [reason.children],
  );

  const title =
    resolveI18n(reason.modifier?.titleI18N) ||
    reason.modifier?.title ||
    t('order.selectReason');

  const description =
    resolveI18n(reason.modifier?.descriptionI18N) ||
    reason.modifier?.description;

  const selectedService = React.useMemo(
    () => services.find((service) => service.id === value),
    [services, value],
  );

  const handleSelect = React.useCallback(() => {
    if (selectedService) {
      onSelect(selectedService);
    }
  }, [onSelect, selectedService]);

  return (
    <Modal
      id="patient-call-selector-modal"
      className={className}
      show={show}
      onHide={onHide}
      title={title}
      footer={
        <Button
          fluid
          size="lg"
          onClick={() => handleSelect()}
          disabled={!selectedService}
        >
          {selectedService?.hasChildren
            ? t('common.advance')
            : t('order.openCall')}
        </Button>
      }
    >
      <div className="px-0">
        {description && (
          <Text
            className="text-gray400"
            weight="light"
            dangerouslySetInnerHTML={{
              __html: htmlParser(description),
            }}
          />
        )}
        <ListGroup className="mt-3" onChange={(v) => setValue(v)} value={value}>
          {services.map((child) => {
            const shortDescription =
              resolveI18n(child.modifier?.shortDescriptionI18N) ||
              child.modifier?.shortDescription;

            return (
              <ListGroup.Item key={child.id} value={child.id}>
                <Text className="d-flex flex-column" weight="semiBold">
                  {child.reasonI18N}
                  {shortDescription && (
                    <Text
                      as="span"
                      className="text-gray400"
                      style={{ marginBottom: '-10px' }}
                    >
                      {shortDescription}
                    </Text>
                  )}
                </Text>
              </ListGroup.Item>
            );
          })}
        </ListGroup>
      </div>
    </Modal>
  );
};

export default PatientCallSelectorModal;
